<template>
  <div v-if="show" class="schedule-send-modal-overlay" @click.self="handleClose">
    <div class="schedule-send-modal">
      <div class="schedule-send-modal__header">
        <h3 class="schedule-send-modal__title">Schedule Send</h3>
        <button
          type="button"
          class="schedule-send-modal__close"
          @click="handleClose"
          :disabled="isLoading"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
      <div class="schedule-send-modal__content">
        <DateSelector
          :model-value="scheduleDate"
          @update:model-value="scheduleDate = $event"
          label="Date"
          :min-date="minDate"
          :disable-past="false"
          required
        />
        <TimeSelector
          :model-value="scheduleTime"
          @update:model-value="scheduleTime = $event"
          label="Time"
          :interval="15"
          :start-hour="0"
          required
        />
        <div v-if="error" class="schedule-send-modal__error">{{ error }}</div>
      </div>
      <div class="schedule-send-modal__actions">
        <Button variant="secondary" size="sm" @click="handleClose" :disabled="isLoading">
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          @click="handleSchedule"
          :disabled="!isValid || isLoading"
          :loading="isLoading"
        >
          Schedule
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { Button, DateSelector, TimeSelector, Icon } from "@/components/ui";

export default {
  name: "ScheduleSendModal",
  components: {
    Icon,
    Button,
    DateSelector,
    TimeSelector,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "schedule"],
  data() {
    const today = new Date();
    const minDateStr = today.toISOString().split("T")[0];

    // Set default time to next hour (or next day if it's late)
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);

    // If next hour is tomorrow, set to tomorrow at 9:00 AM
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split("T")[0];

    let defaultDate = minDateStr;
    let defaultTime = `${String(nextHour.getHours()).padStart(2, "0")}:${String(nextHour.getMinutes()).padStart(2, "0")}`;

    // If next hour is tomorrow, use tomorrow as default date
    if (nextHour.getDate() !== today.getDate()) {
      defaultDate = tomorrowStr;
      defaultTime = "09:00";
    }

    return {
      scheduleDate: defaultDate,
      scheduleTime: defaultTime,
      error: "",
      minDate: minDateStr,
    };
  },
  computed: {
    isValid() {
      if (!this.scheduleDate || !this.scheduleTime) {
        return false;
      }

      const scheduledDateTime = new Date(`${this.scheduleDate}T${this.scheduleTime}`);
      const now = new Date();

      return scheduledDateTime > now;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        // Set default time to next hour (or next day if it's late)
        const now = new Date();
        const nextHour = new Date(now);
        nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);

        // If next hour is tomorrow, set to tomorrow at 9:00 AM
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split("T")[0];

        if (nextHour.getDate() !== today.getDate()) {
          this.scheduleDate = tomorrowStr;
          this.scheduleTime = "09:00";
        } else {
          this.scheduleDate = todayStr;
          this.scheduleTime = `${String(nextHour.getHours()).padStart(2, "0")}:${String(nextHour.getMinutes()).padStart(2, "0")}`;
        }

        this.error = "";
        this.minDate = todayStr;
      }
    },
    scheduleDate() {
      this.validate();
    },
    scheduleTime() {
      this.validate();
    },
  },
  methods: {
    validate() {
      this.error = "";

      if (!this.scheduleDate || !this.scheduleTime) {
        return;
      }

      const scheduledDateTime = new Date(`${this.scheduleDate}T${this.scheduleTime}`);
      const now = new Date();

      if (scheduledDateTime <= now) {
        this.error = "Scheduled time must be in the future";
      }
    },
    handleClose() {
      if (this.isLoading) {
        return; // Prevent closing while loading
      }
      this.$emit("close");
    },
    handleSchedule() {
      if (!this.isValid || this.isLoading) {
        if (!this.isValid) {
          this.validate();
        }
        return;
      }

      const scheduledAt = new Date(`${this.scheduleDate}T${this.scheduleTime}`).toISOString();

      this.$emit("schedule", {
        scheduledAt,
      });

      // Don't close immediately - let parent handle closing after request completes
    },
  },
};
</script>

<style scoped>
.schedule-send-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.schedule-send-modal {
  background: #ffffff;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.schedule-send-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.schedule-send-modal__title {
  font-size: 14px;
  font-weight: 500;
  color: #202124;
  margin: 0;
}

.schedule-send-modal__close {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 18px;
}

.schedule-send-modal__close:hover {
  background: #f1f3f4;
  color: #202124;
}

.schedule-send-modal__content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-send-modal__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}

.schedule-send-modal__error {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: -8px;
}
</style>
